import { useState, useEffect } from 'react'
import {
  Card,
  CardContent,
  TextField,
  Button,
  Checkbox,
  Box,
  Typography,
  Snackbar,
  Alert,
  CircularProgress,
  Autocomplete,
  Chip,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Tooltip,
  Stack,
  IconButton,
  Popover,
  Badge,
} from '@mui/material'
import { formatForTzInput, parseFromTzInput } from '../utils/timestamp'
import { Download, Search, Sensors as SensorsIcon, FilterList } from '@mui/icons-material'
import { useDemoMode } from '../contexts/demo-mode-context'
import { demoWaterLabelFromMap, useDemoWaterLabelBySensorId } from '../hooks/use-demo-water-labels'

const TIMEZONES = [
  { value: 'UTC', label: 'UTC' },
  { value: 'America/New_York', label: 'Eastern (ET)' },
  { value: 'America/Chicago', label: 'Central (CT)' },
  { value: 'America/Denver', label: 'Mountain (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific (PT)' },
  { value: 'America/Anchorage', label: 'Alaska (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii (HT)' },
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Central Europe (CET)' },
  { value: 'Europe/Helsinki', label: 'Eastern Europe (EET)' },
  { value: 'Asia/Dubai', label: 'Dubai (GST)' },
  { value: 'Asia/Kolkata', label: 'India (IST)' },
  { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
  { value: 'Australia/Sydney', label: 'Sydney (AET)' },
  { value: 'Pacific/Auckland', label: 'Auckland (NZST)' },
]

function HistoricalControls({ 
  availableFields, 
  selectedFields, 
  onFieldChange, 
  onLoadData, 
  onDownloadCSV,
  loading,
  hasData,
  maxTimeRangeHours = 12, // Default to 12 if not provided
  sensors = [],
  selectedSensorId,
  onSensorChange,
  groupSensorsByCustomer = false,
  aggregationInterval = 'raw',
  onAggregationChange,
  timezone = 'UTC',
  onTimezoneChange,
  /** Optional flex `sx` overrides: sensor, dataFields, startTime, endTime, timezone, chartIcon, actions */
  filterGrid = {},
}) {
  const { redactionActive } = useDemoMode()
  const demoWaterIndexBySensorId = useDemoWaterLabelBySensorId(sensors)
  const demoWaterLabel = (id) => demoWaterLabelFromMap(demoWaterIndexBySensorId, id)

  /** Filter row: single horizontal line (scrolls on very narrow viewports) */
  const flexSensor = {
    flex: '1 1 0%',
    minWidth: 140,
    maxWidth: 360,
    ...filterGrid.sensor,
  }
  const flexDataFields = { flex: '0 1 200px', minWidth: 160, maxWidth: 260, ...filterGrid.dataFields }
  const flexStartTime = {
    flex: '0 0 auto',
    width: 220,
    minWidth: 200,
    ...filterGrid.startTime,
  }
  const flexEndTime = {
    flex: '0 0 auto',
    width: 220,
    minWidth: 200,
    ...filterGrid.endTime,
  }
  /** Outer column stretches with row; inner box is 40px to match small OutlinedInput */
  const flexTimezone = {
    flex: '0 0 auto',
    width: 200,
    minWidth: 170,
    ...filterGrid.timezone,
  }
  const flexChartIcon = {
    flex: '0 0 auto',
    alignSelf: 'stretch',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    ...filterGrid.chartIcon,
  }
  const flexActions = {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 1.5,
    width: '100%',
    ...filterGrid.actions,
  }

  // Internal state: UTC ISO strings so timezone changes re-render correctly
  const [startTimeUTC, setStartTimeUTC] = useState('')
  const [endTimeUTC, setEndTimeUTC] = useState('')
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' })
  const [chartPopoverAnchor, setChartPopoverAnchor] = useState(null)

  useEffect(() => {
    const now = new Date()
    const defaultStart = new Date(now.getTime() - 24 * 60 * 60 * 1000)
    setEndTimeUTC(now.toISOString())
    setStartTimeUTC(defaultStart.toISOString())
  }, [maxTimeRangeHours])

  // Derive display values in the active timezone for the datetime-local inputs
  const startTimeDisplay = startTimeUTC ? formatForTzInput(new Date(startTimeUTC), timezone) : ''
  const endTimeDisplay = endTimeUTC ? formatForTzInput(new Date(endTimeUTC), timezone) : ''
  
  const showError = (message) => {
    setSnackbar({ open: true, message, severity: 'error' })
  }

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false })
  }

  const handleLoadData = () => {
    if (!startTimeUTC || !endTimeUTC) {
      showError('Please select both start and end times')
      return
    }

    const diff = new Date(endTimeUTC) - new Date(startTimeUTC)
    const maxDiff = maxTimeRangeHours * 60 * 60 * 1000
    
    if (diff > maxDiff) {
      const timeLimit = maxTimeRangeHours >= 24 
        ? `${maxTimeRangeHours / 24} days`
        : `${maxTimeRangeHours} hours`
      showError(`Time range cannot exceed ${timeLimit}`)
      return
    }

    if (diff <= 0) {
      showError('End time must be after start time')
      return
    }

    onLoadData(startTimeUTC, endTimeUTC)
  }

  const fields = [
    { key: 'alkalinity_ppm', label: 'Alkalinity (ppm)' },
    { key: 'ph', label: 'pH' },
    { key: 'temperature', label: 'Temperature (°C)' },
    { key: 'conductivity', label: 'Conductivity (mS/cm)' }
  ]

  const selectedSensor = sensors.find(s => s.sensor_id === selectedSensorId)

  /** Full detail for tooltips / a11y */
  const sensorDetailLabel = (option) => {
    if (!option) return ''
    if (redactionActive) {
      return `${option.sensor_id} · ${demoWaterLabel(option.sensor_id)}`
    }
    const loc = option.sensor_location?.trim()
    const id = option.sensor_id
    if (loc && id) return `${loc} · ${id}`
    return loc || id || ''
  }

  /** Collapsed input: location only (ID in dropdown rows) */
  const sensorDisplayLabel = (option) => {
    if (!option) return ''
    return option.sensor_location?.trim() || option.sensor_id || ''
  }

  const availableFieldOptions = fields.filter(f => availableFields[f.key]).map(f => f.key)
  /** Show data-field picker only when there is a real choice (2+ options) */
  const showDataFieldsPicker = availableFieldOptions.length > 1

  const onlyAvailableFieldKey = availableFieldOptions.length === 1 ? availableFieldOptions[0] : null

  useEffect(() => {
    if (!onlyAvailableFieldKey || !onFieldChange) return
    onFieldChange({
      alkalinity_ppm: onlyAvailableFieldKey === 'alkalinity_ppm',
      ph: onlyAvailableFieldKey === 'ph',
      temperature: onlyAvailableFieldKey === 'temperature',
      conductivity: onlyAvailableFieldKey === 'conductivity',
    })
  }, [onlyAvailableFieldKey, onFieldChange])

  const chartOptionsActive = aggregationInterval !== 'raw'

  return (
    <>
      <Card sx={{ mb: 3, overflow: 'visible' }}>
        <CardContent sx={{ p: 2.5 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, width: '100%' }}>
          {/* Row 1: filters only — nowrap + horizontal scroll if needed */}
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'nowrap',
              // stretch: equal column heights; chart icon column bottom-aligns 40px box to inputs
              alignItems: 'stretch',
              gap: 2,
              width: '100%',
              minWidth: 0,
              overflowX: 'auto',
              overflowY: 'visible',
              py: 2,
              WebkitOverflowScrolling: 'touch',
              '&::-webkit-scrollbar': { height: 6 },
              '&::-webkit-scrollbar-thumb': {
                borderRadius: 3,
                bgcolor: 'action.hover',
              },
            }}
          >
            {/* Sensor Selection */}
            <Box sx={flexSensor}>
              <Tooltip
                title={selectedSensor ? sensorDetailLabel(selectedSensor) : ''}
                placement="bottom-start"
                disableHoverListener={!selectedSensor}
                enterDelay={400}
              >
                <span style={{ display: 'block', width: '100%' }}>
                  <Autocomplete
                    fullWidth
                    value={selectedSensor || null}
                    onChange={(event, newValue) => {
                      if (newValue && onSensorChange) {
                        onSensorChange(newValue.sensor_id)
                      }
                    }}
                    options={sensors}
                    groupBy={groupSensorsByCustomer ? (option) => option.customerName : undefined}
                    getOptionLabel={(option) => {
                      if (!option) return ''
                      if (redactionActive) {
                        return `${option.sensor_id} — ${demoWaterLabel(option.sensor_id)}`
                      }
                      if (groupSensorsByCustomer) {
                        return `${option.customerName} — ${sensorDisplayLabel(option)}`
                      }
                      return sensorDisplayLabel(option)
                    }}
                    isOptionEqualToValue={(option, value) => option.sensor_id === value.sensor_id}
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        minHeight: 40,
                      },
                      '& .MuiAutocomplete-input': {
                        minWidth: '0 !important',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      },
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Sensor"
                        placeholder="Select..."
                        inputProps={{
                          ...params.inputProps,
                          title: selectedSensor ? sensorDetailLabel(selectedSensor) : undefined,
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <Box
                        component="li"
                        {...props}
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'flex-start !important',
                          py: 1.5,
                          px: 2,
                          width: '100%',
                          maxWidth: '100%',
                          boxSizing: 'border-box',
                          '&:hover': {
                            backgroundColor: 'action.hover',
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, width: '100%', minWidth: 0 }}>
                          <Chip
                            label={
                              redactionActive
                                ? demoWaterLabel(option.sensor_id)
                                : option.sensor_location || 'No location'
                            }
                            size="small"
                            color="primary"
                            sx={{
                              fontWeight: 'bold',
                              fontFamily: 'monospace',
                              maxWidth: '100%',
                              height: 'auto',
                              '& .MuiChip-label': {
                                whiteSpace: 'normal',
                                wordBreak: 'break-word',
                                display: 'block',
                                py: 0.5,
                              },
                            }}
                          />
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mt: 0.5, minWidth: 0, width: '100%' }}>
                          <SensorsIcon sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0, mt: 0.25 }} />
                          <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                            {option.sensor_id}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                    ListboxProps={{
                      sx: { maxWidth: '100%' },
                    }}
                  />
                </span>
              </Tooltip>
            </Box>

            {/* Fields Selection — only when 2+ metrics so there is a real choice */}
            {showDataFieldsPicker && (
            <Box sx={flexDataFields}>
                  <Autocomplete
                    fullWidth
                    multiple
                    value={Object.keys(selectedFields).filter(key => selectedFields[key] && availableFields[key])}
                    onChange={(event, newValue) => {
                      const newFields = {
                        alkalinity_ppm: newValue.includes('alkalinity_ppm'),
                        ph: newValue.includes('ph'),
                        temperature: newValue.includes('temperature'),
                        conductivity: newValue.includes('conductivity')
                      }
                      onFieldChange(newFields)
                    }}
                    options={availableFieldOptions}
                    getOptionLabel={(option) => fields.find(f => f.key === option)?.label || option}
                    disableCloseOnSelect
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        height: '40px',
                        overflow: 'hidden',
                        padding: '0 9px !important',
                        '& .MuiAutocomplete-input': {
                          padding: '7.5px 4px 7.5px 6px !important',
                          minWidth: '30px !important',
                        }
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Data Fields"
                        placeholder="Select fields..."
                      />
                    )}
                    renderTags={(value, getTagProps) => (
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 0.5,
                          overflow: 'hidden',
                          flexWrap: 'nowrap',
                          maxWidth: 'calc(100% - 40px)',
                        }}
                      >
                        {value.map((option, index) => (
                          <Chip
                            {...getTagProps({ index })}
                            key={option}
                            label={fields.find(f => f.key === option)?.label}
                            size="small"
                            sx={{
                              height: '24px',
                              fontSize: '0.75rem',
                            }}
                            onDelete={() => {
                              const newValue = value.filter(v => v !== option)
                              const newFields = {
                                alkalinity_ppm: newValue.includes('alkalinity_ppm'),
                                ph: newValue.includes('ph'),
                                temperature: newValue.includes('temperature'),
                                conductivity: newValue.includes('conductivity')
                              }
                              onFieldChange(newFields)
                            }}
                          />
                        ))}
                      </Box>
                    )}
                    renderOption={(props, option, { selected }) => (
                      <li {...props}>
                        <Checkbox
                          checked={selected}
                          size="small"
                          sx={{ mr: 1 }}
                        />
                        <Typography variant="body2">
                          {fields.find(f => f.key === option)?.label}
                        </Typography>
                      </li>
                    )}
                  />
            </Box>
            )}

            {/* Start Time */}
            <Box sx={flexStartTime}>
              <TextField
                fullWidth
                label="Start Time"
                type="datetime-local"
                value={startTimeDisplay}
                onChange={(e) => {
                  const utc = parseFromTzInput(e.target.value, timezone)
                  if (utc) setStartTimeUTC(utc)
                }}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            </Box>

            {/* End Time */}
            <Box sx={flexEndTime}>
              <TextField
                fullWidth
                label="End Time"
                type="datetime-local"
                value={endTimeDisplay}
                onChange={(e) => {
                  const utc = parseFromTzInput(e.target.value, timezone)
                  if (utc) setEndTimeUTC(utc)
                }}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            </Box>

            {/* Timezone */}
            <Box sx={flexTimezone}>
              <FormControl fullWidth size="small">
                <InputLabel id="timezone-label">Timezone</InputLabel>
                <Select
                  labelId="timezone-label"
                  label="Timezone"
                  value={timezone}
                  onChange={(e) => onTimezoneChange?.(e.target.value)}
                >
                  {TIMEZONES.map(tz => (
                    <MenuItem key={tz.value} value={tz.value}>{tz.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box sx={flexChartIcon}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxSizing: 'border-box',
                }}
              >
                <Tooltip title="Chart display — aggregation & smoothing">
                  <Badge
                    color="primary"
                    variant="dot"
                    invisible={!chartOptionsActive}
                    sx={{ flexShrink: 0 }}
                  >
                    <IconButton
                      size="small"
                      aria-label="Open chart display options"
                      onClick={(e) => setChartPopoverAnchor(e.currentTarget)}
                      sx={(theme) => ({
                        width: 40,
                        height: 40,
                        minWidth: 40,
                        padding: 0,
                        border: '1px solid',
                        borderColor:
                          theme.palette.mode === 'dark'
                            ? 'rgba(255, 255, 255, 0.23)'
                            : 'rgba(0, 0, 0, 0.23)',
                        borderRadius: 1,
                        color: theme.palette.text.secondary,
                        bgcolor: 'background.paper',
                        boxSizing: 'border-box',
                        '&:hover': {
                          bgcolor: 'action.hover',
                          borderColor: theme.palette.text.primary,
                          color: theme.palette.text.primary,
                        },
                      })}
                    >
                      <FilterList fontSize="small" />
                    </IconButton>
                  </Badge>
                </Tooltip>
              </Box>
            </Box>

            <Popover
              open={Boolean(chartPopoverAnchor)}
              anchorEl={chartPopoverAnchor}
              onClose={() => setChartPopoverAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              slotProps={{
                paper: {
                  sx: { mt: 1, p: 2, minWidth: 280, maxWidth: 'min(100vw - 24px, 360px)' },
                },
              }}
            >
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Chart display
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                Aggregation and smoothing apply to the graphs below.
              </Typography>
              <Stack spacing={2}>
                <FormControl fullWidth size="small">
                  <InputLabel id="aggregation-label">Aggregation</InputLabel>
                  <Select
                    labelId="aggregation-label"
                    label="Aggregation"
                    value={aggregationInterval}
                    onChange={(e) => onAggregationChange?.(e.target.value)}
                  >
                    <MenuItem value="raw">Raw</MenuItem>
                    <MenuItem value="10min">10 min avg</MenuItem>
                    <MenuItem value="60min">60 min avg</MenuItem>
                    <MenuItem value="1day">1 day avg</MenuItem>
                    <MenuItem value="1week">1 week avg</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
            </Popover>
          </Box>

          {/* Row 2: range hint + actions */}
          <Box sx={flexActions}>
            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
              Max range: {maxTimeRangeHours >= 24 ? `${maxTimeRangeHours / 24} days` : `${maxTimeRangeHours} hours`}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
              <Button
                variant="outlined"
                startIcon={<Download />}
                onClick={() => onDownloadCSV(startTimeUTC, endTimeUTC)}
                disabled={!hasData}
                size="small"
              >
                Download CSV
              </Button>
              <Button
                variant="contained"
                startIcon={loading ? <CircularProgress size={20} /> : <Search />}
                onClick={handleLoadData}
                disabled={loading}
                size="small"
              >
                {loading ? 'Loading' : 'Load Data'}
              </Button>
            </Box>
          </Box>
          </Box>
        </CardContent>
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  )
}

export default HistoricalControls
